import { useEffect, useCallback } from 'react';

import { STORAGE_KEY_APP_FAVICON } from 'src/config/storage-keys';
import { useWhitelabelStore } from 'src/store/whitelabel/whitelabel.store';

const persistFavicon = (faviconUrl: string): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY_APP_FAVICON, faviconUrl);
  } catch {
    // Mantém o favicon funcional quando o armazenamento não estiver disponível.
  }
};

const clearPersistedFavicon = (): void => {
  try {
    window.localStorage.removeItem(STORAGE_KEY_APP_FAVICON);
  } catch {
    // Mantém o favicon funcional quando o armazenamento não estiver disponível.
  }
};

/**
 * Hook para aplicar dinamicamente o favicon baseado no estado do whitelabel
 * Mantém o favicon original até que o novo esteja carregado
 */
export function useDynamicFavicon() {
  const { whitelabel } = useWhitelabelStore();

  // Função para precarregar uma imagem para evitar flash
  const preloadImage = useCallback(
    (src: string): Promise<void> =>
      new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = reject;
        img.src = src;

        // Timeout para evitar espera infinita
        setTimeout(() => reject(new Error('Timeout loading image')), 10000);
      }),
    []
  );

  // Função para obter o tipo MIME baseado na extensão do arquivo
  const getFaviconType = useCallback((url: string): string => {
    const extension = url.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'png':
        return 'image/png';
      case 'svg':
        return 'image/svg+xml';
      case 'gif':
        return 'image/gif';
      case 'jpg':
      case 'jpeg':
        return 'image/jpeg';
      default:
        return 'image/x-icon';
    }
  }, []);

  // Função para atualizar o favicon
  const updateFavicon = useCallback(
    async (faviconUrl: string) => {
      try {
        // Valida a URL
        if (!faviconUrl || typeof faviconUrl !== 'string') {
          throw new Error('URL do favicon inválida');
        }

        // Precarrega a nova imagem
        await preloadImage(faviconUrl);

        // Busca todos os elementos de favicon
        const faviconLinks = document.querySelectorAll("link[rel*='icon']");

        // Se não encontrou nenhum link de favicon, cria um novo
        if (faviconLinks.length === 0) {
          const newFaviconLink = document.createElement('link');
          newFaviconLink.rel = 'icon';
          newFaviconLink.type = getFaviconType(faviconUrl);
          newFaviconLink.href = faviconUrl;
          document.head.appendChild(newFaviconLink);
          persistFavicon(faviconUrl);
          return;
        }

        // Atualiza todos os elementos de favicon encontrados
        faviconLinks.forEach((element) => {
          const faviconLink = element as HTMLLinkElement;
          // Armazena o favicon original se ainda não foi armazenado
          if (!faviconLink.dataset.original) {
            faviconLink.dataset.original = faviconLink.href;
            faviconLink.dataset.originalType = faviconLink.type || 'image/x-icon';
          }

          // Aplica o novo favicon
          faviconLink.href = faviconUrl;
          faviconLink.type = getFaviconType(faviconUrl);

          // Força o navegador a atualizar o favicon
          // Remove e readiciona o elemento
          const newLink = faviconLink.cloneNode(true) as HTMLLinkElement;
          faviconLink.parentNode?.removeChild(faviconLink);
          document.head.appendChild(newLink);
        });

        persistFavicon(faviconUrl);

        // console.log('Favicon atualizado com sucesso:', faviconUrl);
      } catch (error) {
        // console.warn('Não foi possível carregar o favicon do whitelabel:', error);
        // Em caso de erro, mantém o favicon original
      }
    },
    [preloadImage, getFaviconType]
  );

  // Função para restaurar o favicon original
  const restoreOriginalFavicon = useCallback(() => {
    const faviconLinks = document.querySelectorAll("link[rel*='icon']");
    let restored = false;

    faviconLinks.forEach((element) => {
      const faviconLink = element as HTMLLinkElement;
      if (faviconLink.dataset.original) {
        faviconLink.href = faviconLink.dataset.original;
        if (faviconLink.dataset.originalType) {
          faviconLink.type = faviconLink.dataset.originalType;
        }
        restored = true;
      }
    });

    if (restored) {
      clearPersistedFavicon();
      // console.log('Favicon original restaurado');
    }
  }, []);

  // Função para verificar se o favicon atual é customizado
  const isUsingCustomFavicon = useCallback(() => {
    const faviconLinks = document.querySelectorAll("link[rel*='icon']");
    return Array.from(faviconLinks).some((element) => (element as HTMLLinkElement).dataset.original);
  }, []);

  useEffect(() => {
    // Whitelabel ainda não carregou (ou falhou): mantém o favicon já aplicado (cache ou padrão)
    if (!whitelabel) {
      return;
    }

    // Whitelabel carregado mas sem favicon: restaura o original se havia um customizado
    if (!whitelabel.favicon) {
      if (isUsingCustomFavicon()) {
        restoreOriginalFavicon();
      }
      return;
    }

    // Aplica o novo favicon
    updateFavicon(whitelabel.favicon);
  }, [whitelabel, updateFavicon, restoreOriginalFavicon, isUsingCustomFavicon]);

  return {
    updateFavicon,
    restoreOriginalFavicon,
    isUsingCustomFavicon,
    currentWhitelabelFavicon: whitelabel?.favicon || null,
  };
}
